import { useQuery } from '@tanstack/react-query';
import { getTaskDetail, getTaskLogs, getPreviousStageSummary } from '../../shared/services/taskService';
import { taskKeys } from '../../shared/keys';
import type { TaskDetailItem } from '../../shared/types/task';

export interface PreviousStageSummary {
  stageName: string;
  assignedUserName: string | null;
  completedAt: string | null;
  durationMinutes: number | null;
}

interface UseMyTaskDetailResult {
  task: TaskDetailItem | null;
  isLoading: boolean;
  error: Error | null;
  logs: any[];
  logsLoading: boolean;
  previousStage: PreviousStageSummary | null;
  previousStageLoading: boolean;
  refetch: () => Promise<void>;
}

export function useMyTaskDetail(taskId: string | null): UseMyTaskDetailResult {
  const taskQuery = useQuery({
    queryKey: taskKeys.detail(taskId ?? ''),
    enabled: Boolean(taskId),
    queryFn: () => getTaskDetail(taskId!),
  });

  const logsQuery = useQuery({
    queryKey: taskKeys.logs(taskId ?? ''),
    enabled: Boolean(taskId),
    queryFn: () => getTaskLogs(taskId!),
    staleTime: 15_000,
  });

  const task = taskQuery.data ?? null;
  const instanceId = task?.instanceId ?? null;
  const stageOrder = (task?.metadata as any)?.blueprint_stage_order ?? null;

  const prevStageQuery = useQuery({
    queryKey: ['prev-stage', instanceId, stageOrder],
    enabled: Boolean(instanceId) && stageOrder != null && stageOrder > 1,
    queryFn: () => getPreviousStageSummary(instanceId!, stageOrder),
    staleTime: 60_000,
  });

  return {
    task,
    isLoading: taskQuery.isPending && !taskQuery.data,
    error: taskQuery.error ?? null,
    logs: logsQuery.data ?? [],
    logsLoading: logsQuery.isPending,
    previousStage: prevStageQuery.data ?? null,
    previousStageLoading: prevStageQuery.isPending,
    refetch: async () => { await taskQuery.refetch(); },
  };
}
